import { useState, useEffect, useCallback } from 'react'
import { useVestigioService } from 'hooks/useVestigioService'
import { useSetorService } from 'hooks/useSetorService'
import useRouter from 'utils/useRouter'
import { useFormState } from 'hooks/useFormState'
import { useAuth } from 'hooks/useAuth'
import { useUsuarioService } from 'hooks/useUsuarioService'
import constants from 'utils/constants'

const schema = {
  observacoesVestigio: {
    presence: {},
  },
  setDest: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  lacre: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
}

const schemaComUser = {
  ...schema,
  usuarioDestino: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
}

export const useEncaminharVestigio = ({ idVestigio, paraUsuario }) => {
  const vestigioService = useVestigioService()
  const { currentUser: usuario, verificarPermissao } = useAuth()
  const setorService = useSetorService()
  const router = useRouter()
  const usuarioService = useUsuarioService()

  const formEncaminhar = useFormState(paraUsuario ? schemaComUser : schema, {
    lacre: null,
    tipoEmbalagem: null,
    outroTipoEmbalagem: null,
    logomarca: null,
    tipoLogomarca: null,
    setDest: null,
    usuarioDestino: paraUsuario ? null : undefined,
  })

  const formEncaminharSituacional = useFormState(
    {
      situacaoVest: {
        presence: { allowEmpty: false, message: 'é obrigatório' },
      },
    },
    {
      situacaoVest: null,
    }
  )

  const [setores, setSetores] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [abrirConfirmarIdentidade, setAbrirConfirmarIdentidade] = useState(false)
  const [processandoDados, setProcessandoDados] = useState(true)
  const [vestigio, setVestigio] = useState(null)
  const [processandoVestigio, setProcessandoVestigio] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [isFormError, setIsFormError] = useState(false)

  const fetchDados = useCallback(async () => {
    if (paraUsuario) {
      const list = await usuarioService.listarUsuarios()
      setUsuarios(list.filter((usu) => usu.id !== usuario.id))
    } else {
      const list = await setorService.listarSetoresAsync()
      setSetores(list)
    }
    setProcessandoDados(false)
  }, [paraUsuario, usuario.id])

  const fetchVestigio = useCallback(async () => {
    const response = await vestigioService.listarVestigioDetalhado(idVestigio, setProcessandoVestigio)

    if (
      !vestigioService.verificarVestigioEmCustodiaDeUsuario(response, usuario) ||
      !verificarPermissao([constants.PERMISSOES.ENCAMINHAR_VESTIGIO])
    ) {
      router.history.push(`/casos/caso/vestigio/${idVestigio}`)
      return
    }

    setVestigio(response)
  }, [idVestigio, usuario, verificarPermissao, router.history])

  const handleSubmit = useCallback(async () => {
    if (paraUsuario) {
      setAbrirConfirmarIdentidade(true)
    } else {
      await vestigioService.encaminharVestigio(
        idVestigio,
        formEncaminhar?.formState?.values?.setDest?.id === 184
          ? { ...formEncaminhar.formState.values, ...formEncaminharSituacional.formState.values }
          : formEncaminhar.formState.values,
        setProcessandoOperacao
      )
      router.history.push(`/casos/caso/vestigio/detalhar/${idVestigio}`)
    }
  }, [formEncaminhar.formState.values, formEncaminharSituacional.formState.values, idVestigio, paraUsuario, router.history])

  const handleSubmitParaUsuario = useCallback(async () => {
    if (vestigio?.setAtual?.id === formEncaminhar.formState.values.setDest?.id) {
      // Passagem de Custódia
      await vestigioService.solicitarPassagemCustodia(idVestigio, { ...formEncaminhar.formState.values }, setProcessandoOperacao)
      await vestigioService.aceitarPassagemCustodiaTerceiros(idVestigio, formEncaminhar.formState.values.usuarioDestino, setProcessandoOperacao)
    } else {
      // Encaminhamento para outro Setor
      await vestigioService.encaminharVestigio(idVestigio, formEncaminhar.formState.values, setProcessandoOperacao)
      await vestigioService.aceitarEncaminhamentoVestigioTerceiros(idVestigio, formEncaminhar.formState.values.usuarioDestino, setProcessandoOperacao)
    }
    router.history.push(`/casos/caso/vestigio/detalhar/${idVestigio}`)
  }, [formEncaminhar.formState.values, idVestigio, vestigio?.setAtual?.id, router.history])

  useEffect(() => {
    fetchVestigio()
  }, [fetchVestigio])

  useEffect(() => {
    fetchDados()
  }, [fetchDados])

  useEffect(() => {
    if (vestigio?.id) {
      formEncaminhar.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          lacre: vestigio.lacre,
          tipoEmbalagem: vestigio.tipoEmbalagem,
          outroTipoEmbalagem: vestigio.outroTipoEmbalagem,
          setAtual: vestigio.setAtual,
        },
      }))
    }
  }, [vestigio])

  useEffect(() => {
    if (
      formEncaminhar.hasErrorWithoutTouch('setDest') ||
      (formEncaminhar?.formState?.values?.setDest?.id === 184 && formEncaminharSituacional.hasErrorWithoutTouch('situacaoVest'))
    ) {
      setIsFormError(true)
    } else {
      setIsFormError(false)
    }
  }, [formEncaminhar, formEncaminharSituacional])

  return {
    formEncaminhar,
    formEncaminharSituacional,
    setores,
    usuarios,
    abrirConfirmarIdentidade,
    setAbrirConfirmarIdentidade,
    processandoDados,
    vestigio,
    processandoVestigio,
    processandoOperacao,
    isFormError,
    handleSubmit,
    handleSubmitParaUsuario,
    router,
  }
}
